import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { AlertTriangle, Lightbulb, Save } from 'lucide-react';
import ModalWrapper, { ModalCard } from '../ModalWrapper';
import UndoRedoButtons from '../UndoRedoButtons';
import TidyTextButton from '../TidyTextButton';
import FieldError from '../FieldError';
import useTextHistory from '../../hooks/useTextHistory';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TypeIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: ${props => props.$color}18;
  border: 1px solid ${props => props.$color}44;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$color};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: ${props => props.$color};
`;


const Textarea = styled.textarea`
  width: 100%;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 14px 16px;
  color: white;
  font-size: 0.95rem;
  font-family: inherit;
  min-height: 100px;
  resize: vertical;
  line-height: 1.5;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.$color}88;
    background: rgba(255, 255, 255, 0.08);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.25);
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
`;

const ToolbarBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  font-size: 0.78rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 11px 22px;
  background: ${props => props.$color};
  border: none;
  border-radius: 10px;
  color: white;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px ${props => props.$color}44;
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const TYPE_META = {
  obstacle: { icon: AlertTriangle, color: '#E94B6E' },
  opportunity: { icon: Lightbulb, color: '#A6D64D' },
};

/**
 * LensItemModal - Minimal modal for adding/editing an obstacle or opportunity.
 * Single description field, type determined by caller. `onSave` may return a
 * promise: if it rejects, the modal stays open with the text and says why.
 */
export default function LensItemModal({
  type = 'obstacle',
  existingItem,
  lensName = '',
  pivotPointTitle = '',
  instanceId,
  onSave,
  onClose,
}) {
  const { t } = useTranslation();
  const [text, setText] = useState(existingItem?.title || existingItem?.description || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const {
    canUndo,
    canRedo,
    pushSnapshot,
    undo,
    redo,
    setCurrentValue,
  } = useTextHistory(setText);
  const isEditing = !!existingItem;
  const meta = TYPE_META[type] || TYPE_META.obstacle;
  const Icon = meta.icon;
  const canSave = text.trim().length > 0 && !saving;

  const titleKey = type === 'obstacle'
    ? (isEditing ? 'lenses.editObstacle' : 'lenses.addObstacle')
    : (isEditing ? 'lenses.editOpportunity' : 'lenses.addOpportunity');

  const lensSuffix = lensName
    ? t('lenses.lensSuffix', { lens: lensName.toLowerCase() })
    : '';

  const prompt = type === 'obstacle'
    ? t('lenses.obstaclePrompt', { title: pivotPointTitle, lensSuffix })
    : t('lenses.opportunityPrompt', { title: pivotPointTitle, lensSuffix });

  useEffect(() => {
    if (existingItem) {
      setText(existingItem.title || existingItem.description || '');
    }
  }, [existingItem]);

  useEffect(() => {
    setCurrentValue(text);
  }, [text, setCurrentValue]);

  const handleUndo = useCallback(() => { undo(); }, [undo]);
  const handleRedo = useCallback(() => { redo(); }, [redo]);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...(existingItem || {}),
        type,
        title: text.trim(),
        description: text.trim(),
        source: existingItem?.source || 'manual',
      });
    } catch (err) {
      setError(err.code === 'LOCKED'
        ? t('lenses.saveLocked')
        : t('lenses.saveFailed', { error: err.message }));
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.metaKey && canSave) handleSave();
  };

  return (
    <ModalWrapper visible onClose={onClose} zIndex={1000} opacity={0.85}>
      <ModalCard size="sm" onClick={e => e.stopPropagation()} style={{ padding: '28px 32px 28px', border: `1px solid ${meta.color}44` }}>
        <Header>
          <TitleRow>
            <TypeIcon $color={meta.color}>
              <Icon size={18} />
            </TypeIcon>
            <Title $color={meta.color}>
              {t(titleKey)}
            </Title>
          </TitleRow>
        </Header>

        {pivotPointTitle && (
          <div style={{ fontSize: '0.82rem', color: 'rgba(255, 255, 255, 0.5)', marginBottom: 8, lineHeight: 1.5 }}>
            {prompt}
          </div>
        )}

        <Textarea
          $color={meta.color}
          value={text}
          onChange={e => { setText(e.target.value); setError(null); }}
          placeholder={type === 'obstacle' ? t('lenses.writeObstacle') : t('lenses.writeOpportunity')}
          onKeyDown={handleKeyDown}
          autoFocus
        />

        <Toolbar>
          <UndoRedoButtons
            canUndo={canUndo}
            canRedo={canRedo}
            onUndo={handleUndo}
            onRedo={handleRedo}
            buttonComponent={ToolbarBtn}
          />
          <TidyTextButton
            text={text}
            instanceId={instanceId}
            onApply={setText}
            onBeforeApply={pushSnapshot}
            buttonComponent={ToolbarBtn}
          />
        </Toolbar>

        <FieldError>{error}</FieldError>

        <Footer>
          <SaveButton $color={meta.color} onClick={handleSave} disabled={!canSave}>
            <Save size={15} />
            {isEditing ? t('common.save') : t('common.add')}
          </SaveButton>
        </Footer>
      </ModalCard>
    </ModalWrapper>
  );
}
